import { useState, useEffect, useCallback } from 'preact/hooks';
import { useAsyncAction } from '../../hooks/use-async-action';
import { getProduct, updateProduct, reEvaluateProduct, deleteProduct } from '../../api/products';
import { Button } from '../../components/button';
import { Card } from '../../components/card';
import { Badge } from '../../components/badge';
import { Spinner } from '../../components/spinner';
import { Message } from '../../components/message';
import { EmptyState } from '../../components/empty-state';
import { DangerZone } from '../../components/danger-zone';
import { Input } from '../../components/input';
import { NutriscoreBadge } from '../../components/nutriscore-badge';
import { ConfidenceMeter } from '../../components/confidence-meter';
import { EntityLink } from '../../components/entity-link';
import type { ProductDetailResponse, ProductDetail as ProductDetailType, Verdict, ProductFields } from '../../api/types';

interface ProductDetailProps {
  barcode: string;
  onBack: () => void;
}

type DetailSubTab = 'info' | 'verdicts' | 'ingredients';

const EDITABLE_FIELDS: { key: keyof ProductFields; label: string }[] = [
  { key: 'name', label: 'Name' },
  { key: 'brand', label: 'Brand' },
  { key: 'image_url', label: 'Image URL' },
  { key: 'countries', label: 'Countries' },
  { key: 'eco_score', label: 'Eco Score' },
  { key: 'ingredients_raw', label: 'Ingredients (raw)' },
  { key: 'nutriscore_grade', label: 'Nutriscore Grade' },
];

function verdictVariant(status: string): string {
  const s = status.toLowerCase();
  if (s === 'halal' || s === 'permissible' || s === 'compliant') return 'ok';
  if (s === 'haram' || s === 'not_permissible' || s === 'non_compliant') return 'error';
  return 'warn';
}

export function ProductDetail({ barcode, onBack }: ProductDetailProps) {
  const [data, setData] = useState<ProductDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editFields, setEditFields] = useState<ProductFields>({});
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [subTab, setSubTab] = useState<DetailSubTab>('info');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getProduct(barcode);
      setData(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load product');
    } finally {
      setLoading(false);
    }
  }, [barcode]);

  useEffect(() => {
    load();
  }, [load]);

  const save = useAsyncAction(async () => {
    const res = await updateProduct(barcode, editFields);
    setData((prev) => prev ? { ...prev, product: res.product } : prev);
    setEditing(false);
    setEditFields({});
    setSuccessMsg('Product updated');
  });

  const reEval = useAsyncAction(async () => {
    await reEvaluateProduct(barcode);
    await load();
    setSuccessMsg('Re-evaluation complete');
  });

  const del = useAsyncAction(async () => {
    await deleteProduct(barcode);
    onBack();
  });

  const startEdit = () => {
    if (!data) return;
    const p = data.product;
    setEditFields({
      name: p.name ?? '',
      brand: p.brand ?? '',
      image_url: p.image_url ?? '',
      countries: p.countries ?? '',
      eco_score: p.eco_score ?? '',
      ingredients_raw: p.ingredients_raw ?? '',
      nutriscore_grade: p.nutriscore_grade ?? '',
    });
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditFields({});
  };

  const setField = (key: keyof ProductFields, value: string) => {
    setEditFields((prev) => ({ ...prev, [key]: value }));
  };

  if (loading) return <Spinner text="Loading product" centered />;
  if (error) return <Message text={error} type="error" />;
  if (!data) return null;

  const { product, verdicts, linkedIngredients } = data;

  return (
    <div>
      {/* Header bar */}
      <div class="toolbar">
        <Button variant="ghost" size="sm" onClick={onBack}>
          &larr; Back to products
        </Button>
        <div style={{ flex: 1 }} />
        {editing ? (
          <div class="toolbar-actions">
            <Button variant="ghost" size="sm" onClick={cancelEdit}>Cancel</Button>
            <Button size="sm" onClick={() => save.execute()} disabled={save.loading}>
              {save.loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        ) : (
          <Button variant="ghost" size="sm" onClick={startEdit}>Edit</Button>
        )}
      </div>

      {successMsg && <Message text={successMsg} type="success" onDismiss={() => setSuccessMsg(null)} />}
      {save.error && <Message text={save.error} type="error" />}
      {reEval.error && <Message text={reEval.error} type="error" />}
      {del.error && <Message text={del.error} type="error" />}

      {/* Structured product header */}
      <Card>
        <div style={{ display: 'flex', gap: 'var(--sp-5)', alignItems: 'flex-start' }}>
          {/* Product image */}
          <div
            style={{
              width: '120px',
              height: '120px',
              borderRadius: 'var(--radius)',
              background: 'var(--bg-surface)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-tertiary)',
              fontSize: '0.72rem',
              flexShrink: 0,
              overflow: 'hidden',
            }}
          >
            {product.image_url ? (
              <img
                src={product.image_url}
                alt={product.name ?? barcode}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                onError={(e) => { (e.currentTarget as HTMLImageElement).replaceWith(document.createTextNode('No image')); }}
              />
            ) : 'No image'}
          </div>

          {/* Product info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{ marginBottom: 'var(--sp-1)' }}>{product.name ?? barcode}</h3>
            {product.brand && (
              <div style={{ fontSize: '0.84rem', color: 'var(--text-secondary)', marginBottom: 'var(--sp-2)' }}>
                {product.brand}
              </div>
            )}
            <div style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.76rem', color: 'var(--text-tertiary)' }}>
                {product.barcode}
              </span>
              <NutriscoreBadge grade={product.nutriscore_grade} />
              {product.source && (
                <Badge label={product.source} variant="info" />
              )}
            </div>
          </div>
        </div>

        {/* Sub-tabs */}
        <div class="detail-tabs" style={{ marginTop: 'var(--sp-4)' }}>
          <button class={`detail-tab ${subTab === 'info' ? 'active' : ''}`} onClick={() => setSubTab('info')}>
            Info
          </button>
          <button class={`detail-tab ${subTab === 'verdicts' ? 'active' : ''}`} onClick={() => setSubTab('verdicts')}>
            Verdicts ({verdicts.length})
          </button>
          <button class={`detail-tab ${subTab === 'ingredients' ? 'active' : ''}`} onClick={() => setSubTab('ingredients')}>
            Ingredients ({linkedIngredients.length})
          </button>
        </div>

        {/* Info tab */}
        {subTab === 'info' && (
          <div>
            {EDITABLE_FIELDS.map(({ key, label }) => (
              <DetailRow
                key={key}
                label={label}
                value={editing ? undefined : (product[key as keyof ProductDetailType] as string | null)}
                editValue={editing ? (editFields[key] ?? '') : undefined}
                onEdit={editing ? (v) => setField(key, v) : undefined}
              />
            ))}
            <DetailRow label="Source" value={product.source} />
            <DetailRow label="Scan Count" value={String(product.scan_count)} />
            <DetailRow label="Quantity" value={product.quantity} />
            <DetailRow label="Created" value={product.created_at} />
            <DetailRow label="Updated" value={product.updated_at} />
          </div>
        )}

        {/* Verdicts tab */}
        {subTab === 'verdicts' && (
          <div>
            <div class="section-head" style={{ marginTop: '4px' }}>
              <div />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => reEval.execute()}
                disabled={reEval.loading}
              >
                {reEval.loading ? 'Re-evaluating...' : 'Re-evaluate'}
              </Button>
            </div>
            {verdicts.length === 0 ? (
              <EmptyState icon="inbox" message="No verdicts for this product" />
            ) : (
              <div class="verdict-grid">
                {verdicts.map((v: Verdict, i: number) => (
                  <div class="verdict-card" key={i}>
                    <div class="verdict-header">
                      <strong class="framework">{v.framework_id}</strong>
                      <Badge label={v.overall_status} variant={verdictVariant(v.overall_status)} />
                    </div>
                    <div class="verdict-summary">
                      <div style={{ marginBottom: 'var(--sp-1)' }}>
                        <ConfidenceMeter confidence={v.confidence} />
                      </div>
                      <div>Evaluator: {v.evaluated_by}</div>
                      <div>Date: {v.evaluated_at}</div>
                      {v.summary && <div style={{ marginTop: '4px' }}>{v.summary}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Ingredients tab */}
        {subTab === 'ingredients' && (
          <div>
            {linkedIngredients.length === 0 ? (
              <EmptyState icon="inbox" message="No linked ingredients" />
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Raw Name</th>
                    <th>Canonical Name</th>
                    <th>E-Number</th>
                  </tr>
                </thead>
                <tbody>
                  {linkedIngredients.map((ing) => (
                    <tr key={ing.position}>
                      <td>{ing.position}</td>
                      <td>{ing.raw_name}</td>
                      <td>
                        {ing.ingredient_canonical_name ? (
                          <EntityLink
                            type="ingredient"
                            id={ing.ingredient_id}
                            label={ing.ingredient_canonical_name}
                          />
                        ) : (
                          '\u2014'
                        )}
                      </td>
                      <td>{ing.ingredient_e_number ?? '\u2014'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </Card>

      {/* Danger zone */}
      <DangerZone
        title="Delete Product"
        description="Permanently remove this product and all related data (ingredients, verdicts, scan history, cache)."
        buttonLabel="Delete Product"
        onAction={() => del.execute()}
        confirmMessage={`Are you sure you want to delete ${product.name ?? barcode}? This will remove the product, its ingredients, verdicts, scan history, and cache entries. This action cannot be undone.`}
      />
    </div>
  );
}

function DetailRow({
  label,
  value,
  editValue,
  onEdit,
}: {
  label: string;
  value?: string | null;
  editValue?: string;
  onEdit?: (value: string) => void;
}) {
  return (
    <div class="detail-row">
      <span class="detail-label">{label}</span>
      {onEdit !== undefined && editValue !== undefined ? (
        <Input value={editValue} onChange={onEdit} />
      ) : (
        <span class="detail-value">{value ?? '\u2014'}</span>
      )}
    </div>
  );
}
